import { useLang } from 'hooks/lang';

import { TaskWithSub, FullTask, taskStatusByCode } from 'entities';

import { Card, Column, Row, IconButton, Button } from 'components/Blocks';
import { UserAvatarGroup } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';

import { ExpandIcon, ChevronRightIcon } from 'components/Icons';

import { TaskOptions } from '../../TaskOptions';
import { SubTasks } from './SubTasks';
import { TaskDeadline } from './Deadline';
import { TaskTags } from './Tags';

import { useProjectProfile } from '../../context';

import { useToggleV2 } from 'hooks';

import { getFirstAndLastName, getFirstWord, isSameText } from 'utils/text';
import { cn } from 'styles';

interface SingleTaskProps {
  task: TaskWithSub | FullTask;
  disabled?: boolean;
}

export function SingleTask({ task, disabled }: SingleTaskProps) {
  const { openTask } = useProjectProfile();

  const [showSubTasks, { toggle }] = useToggleV2();

  const { t } = useLang();

  return (
    <>
      <Card flow="column" styles={{ borderColor: taskStatusByCode[task.status].color }}>
        <Row justifyContent="space-between" alignItems="flex-start">
          <Row gap="2" alignItems="flex-start" className="small_phone:w-full small_phone:flex-col">
            <IconButton
              tooltipContent={t('view-subtasks')}
              disabled={!task.subTasks.length}
              onClick={toggle}
              className="phone:hidden"
            >
              <ChevronRightIcon
                className={cn('transition-transform', showSubTasks && 'rotate-90')}
              />
            </IconButton>

            <Column gap="2">
              <Row gap="2" flexWrap="wrap">
                <TaskDeadline task={task} className="small_tablet:order-2" />

                <span
                  className="cursor-pointer text-lg font-semibold hover:underline small_tablet:order-1 phone:text-base"
                  onClick={() => !disabled && openTask(task.id)}
                >
                  {`${task.name}${
                    isSameText(task.name, task.baseName) ? '' : ` (${task.baseName})`
                  }`}
                </span>

                <TaskTags className="small_tablet:order-3 small_tablet:max-w-24" task={task} />
              </Row>

              <Row gap="2">
                <UserAvatarGroup
                  users={[task.owner, task.subOwner]}
                  size={32}
                  className="justify-center"
                  avatarClassName="text-sm"
                  showNameOnHover={false}
                />

                <p className="text-start">
                  {task.subOwner
                    ? `${getFirstWord(task.owner.name)} & ${getFirstWord(task.subOwner.name)}`
                    : getFirstAndLastName(task.owner.name)}
                </p>
              </Row>

              {!!task.subTasks.length && (
                <Button
                  variant="ghost"
                  size="small"
                  className="hidden w-fit justify-start px-0 pr-2 phone:flex"
                  color="gray"
                  onClick={toggle}
                >
                  <ChevronRightIcon
                    className={cn('transition-transform', showSubTasks && 'rotate-90')}
                  />
                  {task.subTasks.length} {t('subtasks')}
                </Button>
              )}
            </Column>
          </Row>

          <Row gap="1" justifyContent="flex-end" className="phone:flex-col phone:items-end">
            <EntityStatusTag
              target="task"
              status={task.status}
              size="medium"
              className="phone:whitespace-nowrap phone:text-xs"
            />

            {!disabled && (
              <>
                <IconButton
                  className="ml-1 phone:hidden"
                  tooltipContent={t('open') as string}
                  onClick={() => openTask(task.id)}
                >
                  <ExpandIcon />
                </IconButton>

                <TaskOptions task={task} />
              </>
            )}
          </Row>
        </Row>

        <SubTasks show={showSubTasks} subTasks={task.subTasks} />
      </Card>
    </>
  );
}
